import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { Lender, LenderProduct } from '../../api/lenders';
import { useProducts } from '../../hooks/useProducts';
import { useRemoveLenderProduct } from '../../hooks/useLenders';
import SuccessToast from '../../components/common/SuccessToast';
import { DEFAULT_TABLE_PAGE_SIZE } from '../../components/common/TablePagination';
import FilterMultiSelect from '../../components/common/FilterMultiSelect';
import filterCloseIcon from '../../assets/filter-close.svg';
import filterTagCloseIcon from '../../assets/filter-tag-close.svg';
import filterResetIcon from '../../assets/filter-reset.svg';
import LenderProductRemoveDialog from './LenderProductRemoveDialog';
import LenderProductAddDrawer from './LenderProductAddDrawer';
import LenderProductEditDrawer from './LenderProductEditDrawer';
import searchIcon from './assets/product-search.svg';
import plusIcon from './assets/product-plus.svg';
import exportIcon from './assets/product-export.svg';
import filterIcon from './assets/product-filter.svg';
import dotsIcon from './assets/product-dots.svg';
import previousIcon from './assets/product-chevron-left.svg';
import nextIcon from './assets/product-chevron-right.svg';

interface ProductRow {
  assignment: LenderProduct;
  service: string;
  category: string;
  provider: string;
}

type SortKey = 'service' | 'category' | 'product' | 'provider';
const PAGE_SIZE = DEFAULT_TABLE_PAGE_SIZE;
const FILTER_FIELDS = [
  { key: 'service', label: 'Service' },
  { key: 'category', label: 'Category' },
  { key: 'product', label: 'Product' },
  { key: 'provider', label: 'Provider' },
] as const;
type FilterKey = (typeof FILTER_FIELDS)[number]['key'];
type ProductFilters = Record<FilterKey, string[]>;
const EMPTY_FILTERS: ProductFilters = { service: [], category: [], product: [], provider: [] };
const filterValue = (row: ProductRow, key: FilterKey): string => key === 'product' ? row.assignment.name : row[key];

export default function LenderProductsTab({ lender }: { lender: Lender }) {
  const { data: catalog, isLoading, isError } = useProducts();
  const removal = useRemoveLenderProduct();
  const [removing, setRemoving] = useState<LenderProduct | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addedSuccess, setAddedSuccess] = useState(false);

  const actionRef = useRef<HTMLButtonElement | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const editMenuRef = useRef<HTMLButtonElement>(null);
  const submittingRef = useRef(false);
  const returnFocus = useCallback(() => {
    if (actionRef.current?.isConnected) actionRef.current.focus();
    else headingRef.current?.focus();
  }, []);
  const confirmRemoval = async () => {
    if (!removing || submittingRef.current) return;
    submittingRef.current = true;
    try {
      await removal.mutateAsync({ urn: lender.urn, productUrn: removing.urn });
      // The removed row can disappear in a later query notification.
      actionRef.current = null;
      setRemoving(null);
      setShowSuccess(true);
    } catch {
      // Keep the dialog open and the assigned product visible so the user can retry.
    } finally {
      submittingRef.current = false;
    }
  };
  const [editing, setEditing] = useState<LenderProduct | null>(null);
  const [openMenu, setOpenMenu] = useState<{ assignment: LenderProduct; top: number; right: number } | null>(null);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; direction: 1 | -1 } | null>(null);
  const [page, setPage] = useState(0);
  const [filterOpen, setFilterOpen] = useState(false);
  const [draftFilters, setDraftFilters] = useState<ProductFilters>(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<ProductFilters>(EMPTY_FILTERS);
  const [expandedFilters, setExpandedFilters] = useState<Set<FilterKey>>(() => new Set());
  const [filterPlacement, setFilterPlacement] = useState({ top: 0, right: 16, maxHeight: 640 });
  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const filterPanelRef = useRef<HTMLDivElement>(null);
  const filterPanelId = useId();

  useEffect(() => {
    if (!filterOpen) return;
    filterPanelRef.current?.querySelector<HTMLButtonElement>('button[aria-expanded]')?.focus();
    const outside = (event: MouseEvent) => {
      const target = event.target;
      if (target instanceof Node && !filterPanelRef.current?.contains(target) && !filterButtonRef.current?.contains(target)) {
        setFilterOpen(false);
      }
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !event.defaultPrevented) {
        setFilterOpen(false);
        filterButtonRef.current?.focus();
      }
    };
    const resize = () => setFilterOpen(false);
    const scroll = (event: Event) => {
      if (event.target instanceof Node && filterPanelRef.current?.contains(event.target)) return;
      setFilterOpen(false);
    };
    document.addEventListener('mousedown', outside);
    document.addEventListener('keydown', escape);
    window.addEventListener('resize', resize);
    window.addEventListener('scroll', scroll, { capture: true, passive: true });
    return () => {
      document.removeEventListener('mousedown', outside);
      document.removeEventListener('keydown', escape);
      window.removeEventListener('resize', resize);
      window.removeEventListener('scroll', scroll, true);
    };
  }, [filterOpen]);

  useEffect(() => {
    if (!openMenu) return;
    editMenuRef.current?.focus();
    const close = () => { setOpenMenu(null); returnFocus(); };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    window.addEventListener('scroll', close, { capture: true, passive: true });
    window.addEventListener('resize', close);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [openMenu, returnFocus]);

  const rows = useMemo(() => (lender.products ?? []).map((assigned): ProductRow => {
    const assignment = typeof assigned === 'string' ? { name: assigned, urn: assigned } : assigned;
    const product = catalog?.find((item) => item.urn === assignment.urn || item.vendorProducts?.some((vendor) => vendor.urn === assignment.urn));
    const vendor = product?.vendorProducts?.find((item) => item.urn === assignment.urn);
    return {
      assignment,
      service: product?.serviceType ?? '—',
      category: product?.productType ?? '—',
      provider: vendor?.vendorName ?? '—',
    };
  }), [lender.products, catalog]);

  const visibleRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = rows.filter((row) => {
      if (!FILTER_FIELDS.every(({ key }) => appliedFilters[key].length === 0 || appliedFilters[key].includes(filterValue(row, key)))) return false;
      return !term || [row.service, row.category, row.assignment.name, row.provider]
        .some((value) => value.toLowerCase().includes(term));
    });
    if (!sort) return filtered;
    const value = (row: ProductRow) => sort.key === 'product' ? row.assignment.name : row[sort.key];
    return [...filtered].sort((a, b) => sort.direction * value(a).localeCompare(value(b)));
  }, [rows, search, sort, appliedFilters]);

  const pageCount = Math.max(1, Math.ceil(visibleRows.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const pagedRows = visibleRows.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);
  const pageStart = Math.max(1, Math.min(currentPage - 1, pageCount - 4));
  const pageNumbers = Array.from({ length: Math.min(pageCount, 5) }, (_, index) => pageStart + index);
  const showingFrom = visibleRows.length === 0 ? 0 : currentPage * PAGE_SIZE + 1;
  const showingTo = Math.min((currentPage + 1) * PAGE_SIZE, visibleRows.length);

  const hasAppliedFilters = FILTER_FIELDS.some(({ key }) => appliedFilters[key].length > 0);
  const filterOptions = (key: FilterKey) => [...new Set(rows.map((row) => filterValue(row, key)))]
    .filter((value) => value && value !== '—')
    .sort((a, b) => a.localeCompare(b))
    .map((value) => ({ value, label: value }));
  const closeFilters = () => {
    setFilterOpen(false);
    filterButtonRef.current?.focus();
  };
  const resetFilters = () => {
    setAppliedFilters(EMPTY_FILTERS);
    setDraftFilters(EMPTY_FILTERS);
    setExpandedFilters(new Set());
    setPage(0);
    if (!filterOpen) filterButtonRef.current?.focus();
  };
  const changeSort = (key: SortKey) => {
    setSort((current) => {
      if (current?.key !== key) return { key, direction: 1 };
      if (current.direction === 1) return { key, direction: -1 };
      return null;
    });
    setPage(0);
  };

  return (
    <section className="rounded-xl border border-[#ddd] bg-surface p-6 text-content dark:border-border-subtle sm:p-10">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 ref={headingRef} tabIndex={-1} className="text-[18px] font-medium leading-[27px]">Products</h2>
        <button type="button" onClick={() => { setFilterOpen(false); setAddedSuccess(false); setShowSuccess(false); setAdding(true); }} className="flex h-[38px] w-[154px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand items-center justify-center gap-2.5 rounded-lg bg-brand text-base font-semibold text-white">
          <img src={plusIcon} alt="" />
          Add Product
        </button>
      </div>

      <div className="mt-8 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <label className="flex h-[43px] w-full max-w-[408px] items-center gap-2 rounded-[5px] border border-border-input bg-surface px-3 focus-within:ring-2 focus-within:ring-brand">
            <img src={searchIcon} alt="" />
            <span className="sr-only">Search lender products</span>
            <input
              type="search"
              value={search}
              onChange={(event) => { setSearch(event.target.value); setPage(0); }}
              placeholder="Search ..."
              className="min-w-0 flex-1 bg-transparent text-sm text-content outline-none placeholder:text-content-secondary"
            />
          </label>
          <div className="relative flex items-center gap-2">
            <button type="button" disabled title="Export is not available yet" aria-label="Export products" className="cursor-not-allowed rounded-[7px] bg-surface"><img src={exportIcon} alt="" /></button>
            <button ref={filterButtonRef} type="button" aria-label="Filter products" aria-expanded={filterOpen} aria-controls={filterPanelId}
              onClick={() => {
                if (filterOpen) { closeFilters(); return; }
                setOpenMenu(null);
                setDraftFilters({ ...appliedFilters });
                setExpandedFilters(new Set());
                const rect = filterButtonRef.current?.getBoundingClientRect();
                if (!rect) return;
                setFilterPlacement({
                  top: rect.bottom + 8,
                  right: Math.max(16, window.innerWidth - rect.right),
                  maxHeight: Math.max(160, window.innerHeight - rect.bottom - 24),
                });
                setFilterOpen(true);
              }}
              className={`rounded-[7px] bg-surface focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand ${filterOpen || hasAppliedFilters ? 'ring-1 ring-brand' : ''}`}><img src={filterIcon} alt="" /></button>
            {filterOpen && (
              <div ref={filterPanelRef} id={filterPanelId} role="dialog" aria-label="Filter" aria-modal="false"
                style={filterPlacement}
                className="fixed z-30 flex w-[278px] max-w-[calc(100vw-2rem)] flex-col rounded-[10px] bg-surface p-[15px] text-content shadow-[0_1px_10px_rgba(0,0,0,0.13)]">
                <div className="mb-3 flex shrink-0 items-start justify-between gap-2 pb-2">
                  <h3 className="text-[17px] font-medium leading-6">Filter</h3>
                  <button type="button" onClick={closeFilters} aria-label="Close product filters"
                    className="flex h-5 w-5 items-center justify-center rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand"><img src={filterCloseIcon} alt="" /></button>
                </div>
                <div className="min-h-0 space-y-3 overflow-y-auto overscroll-contain">
                  {FILTER_FIELDS.map(({ key, label }) => (
                    <div key={key} className="space-y-1">
                      <p className="text-xs font-medium leading-4">{label}</p>
                      <FilterMultiSelect label={label} size="compact" options={filterOptions(key)} selected={draftFilters[key]}
                        onChange={(selected) => setDraftFilters((current) => ({ ...current, [key]: selected }))}
                        open={expandedFilters.has(key)} onOpenChange={(open) => setExpandedFilters((current) => {
                          const next = new Set(current);
                          if (open) next.add(key); else next.delete(key);
                          return next;
                        })} />
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex shrink-0 items-center justify-between gap-2 pt-2">
                  <button type="button" onClick={resetFilters} className="h-[30px] w-[82px] rounded-[5px] border border-border text-xs font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand">Reset</button>
                  <button type="button" onClick={() => { setAppliedFilters({ ...draftFilters }); setPage(0); closeFilters(); }}
                    className="h-[30px] w-[82px] rounded-[5px] border border-brand text-xs font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand">Apply</button>
                </div>
              </div>
            )}
          </div>
        </div>

        {hasAppliedFilters && (
          <div role="group" aria-label="Applied product filters" className="flex flex-wrap items-center justify-end gap-[15px]">
            <div className="flex flex-wrap items-center gap-2.5">
              {FILTER_FIELDS.filter(({ key }) => appliedFilters[key].length > 0).map(({ key, label }) => (
                <span key={key} className="inline-flex h-[30px] items-center gap-2 rounded-[5px] bg-[#3a149e] px-2 text-[10px] font-semibold leading-[18px] text-white">
                  {label} ({appliedFilters[key].length} Selected)
                  <button type="button" aria-label={`Remove ${label} filter`} onClick={() => {
                    setAppliedFilters((current) => ({ ...current, [key]: [] }));
                    setDraftFilters((current) => ({ ...current, [key]: [] }));
                    setPage(0);
                    filterButtonRef.current?.focus();
                  }} className="flex h-5 w-5 items-center justify-center rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"><img src={filterTagCloseIcon} alt="" /></button>
                </span>
              ))}
            </div>
            <button type="button" onClick={resetFilters} className="inline-flex items-center gap-2 rounded text-[10px] leading-[18px] text-content-secondary hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand">
              <img src={filterResetIcon} alt="" />Reset to default
            </button>
          </div>
        )}
        {isError && <p role="alert" className="text-sm text-red-600">Product details could not be loaded. Assigned products are still shown.</p>}
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full min-w-[680px] table-fixed border-collapse text-left">
            <colgroup><col /><col /><col /><col /><col className="w-[26px]" /></colgroup>
            <thead className="table-head text-white">
              <tr className="h-[42px]">
                {([['service', 'Service'], ['category', 'Category'], ['product', 'Product'], ['provider', 'Provider']] as [SortKey, string][]).map(([key, label]) => (
                  <th key={key} scope="col" aria-sort={sort?.key === key ? (sort.direction === 1 ? 'ascending' : 'descending') : 'none'} className="px-4 text-xs font-semibold">
                    <button type="button" onClick={() => changeSort(key)} className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white">
                      {label}
                      <svg width="10" height="14" viewBox="0 0 10 14" fill="none" aria-hidden="true" focusable="false" className="shrink-0">
                        <path d="M4.70618 2.31807C4.86455 2.14663 5.13545 2.14663 5.29382 2.31807L7.84387 5.07858C8.08053 5.33477 7.89882 5.75 7.55005 5.75H2.44995C2.10118 5.75 1.91947 5.33477 2.15613 5.07858L4.70618 2.31807Z" fill="white" fillOpacity={sort?.key === key && sort.direction === 1 ? 1 : 0.6} />
                        <path d="M5.29382 11.6819C5.13545 11.8534 4.86455 11.8534 4.70618 11.6819L2.15613 8.92142C1.91947 8.66523 2.10118 8.25 2.44995 8.25H7.55005C7.89882 8.25 8.08053 8.66523 7.84387 8.92142L5.29382 11.6819Z" fill="white" fillOpacity={sort?.key === key && sort.direction === -1 ? 1 : 0.6} />
                      </svg>
                    </button>
                  </th>
                ))}
                <th scope="col"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody className="bg-surface text-[13px] leading-5 text-content-secondary">
              {isLoading ? (
                <tr><td colSpan={5} className="px-4 py-6 text-center">Loading products...</td></tr>
              ) : pagedRows.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-6 text-center">No products found.</td></tr>
              ) : pagedRows.map((row) => (
                <tr key={row.assignment.urn} className={`h-11 border-b border-gray-200 last:border-b-0 dark:border-border-subtle${openMenu?.assignment.urn === row.assignment.urn ? 'outline outline-2 -outline-offset-2 outline-brand-deep' : ''}`}>
                  <td className="truncate px-4" title={row.service}>{row.service}</td>
                  <td className="truncate px-4" title={row.category}>{row.category}</td>
                  <td className="truncate px-4" title={row.assignment.name}>{row.assignment.name}</td>
                  <td className="truncate px-4" title={row.provider}>{row.provider}</td>
                  <td className="text-center">
                    <button
                      type="button"
                      aria-label={`Actions for ${row.assignment.name}`}
                      aria-expanded={openMenu?.assignment.urn === row.assignment.urn}
                      onClick={(event) => {
                        setFilterOpen(false);
                        actionRef.current = event.currentTarget;
                        const rect = event.currentTarget.getBoundingClientRect();
                        setOpenMenu((current) => current?.assignment.urn === row.assignment.urn
                          ? null
                          : { assignment: row.assignment, top: Math.max(8, Math.min(rect.bottom, window.innerHeight - 98)), right: Math.max(8, window.innerWidth - rect.right) });
                      }}
                      className="flex h-7 w-5 items-center justify-center rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand"
                    ><img src={dotsIcon} alt="" className="rotate-90" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-end justify-between gap-3 pt-4 text-[13px] text-content-secondary">
          <p>Showing {showingFrom}-{showingTo} of {visibleRows.length} results</p>
          <nav aria-label="Product pages" className="flex items-center gap-2">
            <button type="button" aria-label="Previous page" disabled={currentPage === 0} onClick={() => setPage(currentPage - 1)} className="flex h-8 w-10 items-center justify-center rounded-md border border-border bg-surface disabled:opacity-50"><img src={previousIcon} alt="" /></button>
            {pageNumbers.map((number) => (
              <button key={number} type="button" aria-label={`Page ${number}`} aria-current={number === currentPage + 1 ? 'page' : undefined} onClick={() => setPage(number - 1)} className={`flex h-8 w-8 items-center justify-center rounded-md border text-[13px] ${number === currentPage + 1 ? 'border-brand-deep bg-brand-deep font-semibold text-white' : 'border-border bg-surface font-medium text-content'}`}>{number}</button>
            ))}
            <button type="button" aria-label="Next page" disabled={currentPage + 1 >= pageCount} onClick={() => setPage(currentPage + 1)} className="flex h-8 w-10 items-center justify-center rounded-md border border-border bg-surface disabled:opacity-50"><img src={nextIcon} alt="" /></button>
          </nav>
        </div>
      </div>

      <div className="mt-10 flex justify-end">
        <button type="button" disabled title="Product changes are saved in the edit drawer" className="flex h-11 min-w-[88px] items-center justify-center rounded-lg bg-[#3a149e] px-6 text-[15px] font-semibold text-white opacity-20">Save</button>
      </div>

      {openMenu && createPortal(
        <>
          <button type="button" aria-label="Close product actions" className="fixed inset-0 z-40 cursor-default" onClick={() => { setOpenMenu(null); returnFocus(); }} />
          <div role="group" aria-label={`Product actions for ${openMenu.assignment.name}`} style={{ top: openMenu.top, right: openMenu.right }} className="fixed z-50 w-[105px] rounded-lg border border-[#e8ebf1] bg-white py-0.5 text-[#273141] shadow-[0_4px_4px_rgba(0,0,0,0.08)]">
            <div className="h-[42px] px-1 py-1"><button ref={editMenuRef} type="button" onClick={() => { setEditing(openMenu.assignment); setOpenMenu(null); }} className="h-full w-full rounded border-[1.5px] border-transparent px-2 text-left text-[15px] leading-[22.5px] hover:bg-[#e8ebf1] hover:text-[#0f1b2f] focus-visible:bg-[#e8ebf1] focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand">Edit</button></div>
            <div className="h-[42px] px-1 py-1"><button type="button" onClick={() => { removal.reset(); setShowSuccess(false); setRemoving(openMenu.assignment); setOpenMenu(null); }} className="h-full w-full rounded border-[1.5px] border-transparent px-2 text-left text-[15px] leading-[22.5px] hover:bg-[#e8ebf1] hover:text-[#0f1b2f] focus-visible:bg-[#e8ebf1] focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand">Remove</button></div>
          </div>
        </>, document.body,
      )}
      {removing && <LenderProductRemoveDialog productName={removing.name} lenderName={lender.displayName || lender.name} isPending={removal.isPending} errorMessage={removal.isError ? 'Product could not be removed. Please try again.' : null} onConfirm={confirmRemoval} onCancel={() => { if (!submittingRef.current) setRemoving(null); }} returnFocus={returnFocus} />}
      {adding && <LenderProductAddDrawer lender={lender} onClose={() => setAdding(false)} onAdded={() => { setAdding(false); setAddedSuccess(true); }} />}
      {addedSuccess && <SuccessToast message="Success: Product Added" onDismiss={() => setAddedSuccess(false)} />}
      {showSuccess && <SuccessToast message="Success: Product Removed" onDismiss={() => setShowSuccess(false)} />}
      {editing && <LenderProductEditDrawer key={editing.urn} lender={lender} product={editing} onClose={() => setEditing(null)} catalog={catalog} />}
    </section>
  );
}
